import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { clientsAPI, miniPellesAPI, devisAPI, serviceCatalogAPI } from '@/lib/api';
import { formatPrice, cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Save,
  Package,
  Users,
  Wrench,
  Loader2,
  Plus,
  Trash2,
  Check,
  ChevronsUpDown,
  FileText,
  X,
} from 'lucide-react';
import {
  SERVICE_LINE_CATEGORIES,
  createEmptyServiceLine,
  normalizeServiceLines,
  calculateServiceQuoteTotals,
} from '@/pages/ServiceQuoteShared';

const DevisCreateSimplePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedClientId = searchParams.get('client');

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [clients, setClients] = useState([]);
  const [miniPelles, setMiniPelles] = useState([]);
  const [serviceCatalog, setServiceCatalog] = useState([]);
  const [clientPopoverOpen, setClientPopoverOpen] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [catalogSelection, setCatalogSelection] = useState('');

  const [form, setForm] = useState({
    client_id: '',
    client_name: '',
    mini_pelle_id: '',
    mini_pelle_name: '',
    mini_pelle_price: 0,
    service_lines: [createEmptyServiceLine()],
    discount: 0,
    discount_type: 'percent',
    notes: '',
    validity_days: 30,
    devis_type: 'machine',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [clientsRes, mpRes, scRes] = await Promise.all([
          clientsAPI.getAll(),
          miniPellesAPI.getAll(),
          serviceCatalogAPI.getAll().catch(() => ({ data: [] })),
        ]);
        setClients(clientsRes.data || []);
        setMiniPelles(mpRes.data || []);
        setServiceCatalog(scRes.data || []);

        if (preselectedClientId) {
          const client = (clientsRes.data || []).find((c) => c.id === preselectedClientId);
          if (client) {
            setForm((prev) => ({
              ...prev,
              client_id: client.id,
              client_name: client.name,
            }));
          }
        }
      } catch {
        toast.error('Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [preselectedClientId]);

  const selectedClient = clients.find((c) => c.id === form.client_id);
  const selectedMachine = miniPelles.find((mp) => mp.id === form.mini_pelle_id);

  const filteredClients = clients.filter((c) => {
    const q = clientSearch.toLowerCase();
    return (
      c.name?.toLowerCase().includes(q) ||
      c.company?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q)
    );
  });

  const lines = useMemo(() => normalizeServiceLines(form.service_lines), [form.service_lines]);
  const totals = useMemo(
    () => calculateServiceQuoteTotals({ service_lines: lines, discount: form.discount, discount_type: form.discount_type }),
    [lines, form.discount, form.discount_type]
  );

  const selectClient = (client) => {
    setForm((prev) => ({ ...prev, client_id: client.id, client_name: client.name }));
    setClientPopoverOpen(false);
    setClientSearch('');
  };

  const selectMachine = (machineId) => {
    if (!machineId || machineId === '__clear__') {
      setForm((prev) => ({ ...prev, mini_pelle_id: '', mini_pelle_name: '', mini_pelle_price: 0 }));
      return;
    }
    const mp = miniPelles.find((m) => m.id === machineId);
    if (mp) {
      setForm((prev) => ({
        ...prev,
        mini_pelle_id: mp.id,
        mini_pelle_name: mp.name,
        mini_pelle_price: mp.base_price || 0,
      }));
    }
  };

  const updateLine = (lineId, key, value) => {
    setForm((prev) => ({
      ...prev,
      service_lines: normalizeServiceLines(prev.service_lines).map((line) =>
        line.id === lineId
          ? { ...line, [key]: key === 'quantity' || key === 'unit_price' ? Number(value || 0) : value }
          : line
      ),
    }));
  };

  const addEmptyLine = () => {
    setForm((prev) => ({
      ...prev,
      service_lines: [...normalizeServiceLines(prev.service_lines), createEmptyServiceLine()],
    }));
  };

  const addCatalogLine = (itemId) => {
    const item = serviceCatalog.find((s) => s.id === itemId);
    if (!item) return;
    const newLine = {
      ...createEmptyServiceLine(),
      catalog_item_id: item.id,
      designation: item.name,
      category: item.category || 'prestation',
      description: item.description || '',
      unit: item.default_unit || 'u',
      unit_price: Number(item.default_price || 0),
      line_total: Number(item.default_price || 0),
    };
    setForm((prev) => ({
      ...prev,
      service_lines: [...normalizeServiceLines(prev.service_lines), newLine],
    }));
    setCatalogSelection('');
  };

  const removeLine = (lineId) => {
    setForm((prev) => ({
      ...prev,
      service_lines: normalizeServiceLines(prev.service_lines).filter((l) => l.id !== lineId),
    }));
  };

  const handleSubmit = async () => {
    if (!form.client_id) {
      toast.error('Sélectionne un client');
      return;
    }
    if (lines.length === 0) {
      toast.error('Ajoute au moins une ligne');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        devis_type: 'machine',
        client_id: form.client_id,
        client_name: form.client_name,
        mini_pelle_id: form.mini_pelle_id || null,
        mini_pelle_name: form.mini_pelle_name || null,
        mini_pelle_price: 0,
        mini_pelle_image: null,
        mini_pelle_description: null,
        mini_pelle_specs: {},
        options: [],
        service_lines: normalizeServiceLines(form.service_lines),
        discount: Number(form.discount || 0),
        discount_type: form.discount_type,
        notes: form.notes || null,
        validity_days: Number(form.validity_days || 30),
        status: 'brouillon',
        show_snc: false,
        show_financement: false,
      };
      const res = await devisAPI.create(payload);
      toast.success('Devis créé avec succès');
      navigate(`/admin/devis/${res.data.id}`);
    } catch {
      toast.error('Erreur lors de la création du devis');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/devis')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
              Nouveau devis
            </h1>
            <p className="text-slate-500 mt-1">Formulaire rapide</p>
          </div>
        </div>
        <Button
          onClick={handleSubmit}
          disabled={submitting}
          className="bg-yellow-500 hover:bg-yellow-600 text-slate-900"
        >
          {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Créer le devis
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Client */}
          <Card className="border-slate-200">
            <CardHeader className="bg-slate-50/50 border-b border-slate-100">
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                <Users className="w-5 h-5 text-yellow-500" />
                Client
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-3">
              <Popover open={clientPopoverOpen} onOpenChange={setClientPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className={cn('w-full justify-between', !form.client_id && 'text-slate-400')}
                  >
                    {selectedClient ? selectedClient.name : 'Rechercher un client...'}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0" align="start">
                  <Command>
                    <CommandInput
                      placeholder="Nom, société, email..."
                      value={clientSearch}
                      onValueChange={setClientSearch}
                    />
                    <CommandList>
                      <CommandEmpty>Aucun client trouvé</CommandEmpty>
                      <CommandGroup>
                        {filteredClients.slice(0, 50).map((client) => (
                          <CommandItem
                            key={client.id}
                            value={client.id}
                            onSelect={() => selectClient(client)}
                          >
                            <Check
                              className={cn('mr-2 h-4 w-4', form.client_id === client.id ? 'opacity-100' : 'opacity-0')}
                            />
                            <div>
                              <p className="font-medium">{client.name}</p>
                              {client.company && <p className="text-xs text-slate-500">{client.company}</p>}
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {selectedClient && (
                <div className="p-3 bg-slate-50 rounded-md text-sm space-y-1">
                  <p className="font-medium text-slate-800">{selectedClient.name}</p>
                  {selectedClient.company && <p className="text-slate-500">{selectedClient.company}</p>}
                  {selectedClient.email && <p className="text-slate-500">{selectedClient.email}</p>}
                  {selectedClient.phone && <p className="text-slate-500">{selectedClient.phone}</p>}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Machine (référence) */}
          <Card className="border-slate-200">
            <CardHeader className="bg-slate-50/50 border-b border-slate-100">
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                <Package className="w-5 h-5 text-yellow-500" />
                Machine (référence)
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-3">
              <div className="flex gap-2">
                <Select value={form.mini_pelle_id || '__clear__'} onValueChange={selectMachine}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Sélectionner une machine du catalogue..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__clear__">— Aucune machine —</SelectItem>
                    {miniPelles.map((mp) => (
                      <SelectItem key={mp.id} value={mp.id}>
                        {mp.name} {mp.model ? `(${mp.model})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {form.mini_pelle_name && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setForm((prev) => ({ ...prev, mini_pelle_id: '', mini_pelle_name: '', mini_pelle_price: 0 }))}
                  >
                    <X className="w-4 h-4 text-slate-400" />
                  </Button>
                )}
              </div>
              {!form.mini_pelle_id && (
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Ou saisir manuellement</Label>
                  <Input
                    placeholder="Ex: Kubota KX016-4, Liugong CLG906F..."
                    value={form.mini_pelle_name}
                    onChange={(e) => setForm((prev) => ({ ...prev, mini_pelle_name: e.target.value }))}
                  />
                </div>
              )}
              {form.mini_pelle_name && (
                <p className="text-sm text-slate-500">
                  Référence : <span className="font-medium text-slate-800">{form.mini_pelle_name}</span>
                  {selectedMachine?.brand ? ` · ${selectedMachine.brand}` : ''}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Lignes */}
          <Card className="border-slate-200">
            <CardHeader className="bg-slate-50/50 border-b border-slate-100">
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                <Wrench className="w-5 h-5 text-yellow-500" />
                Lignes du devis
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              <div className="flex flex-col md:flex-row gap-3">
                {serviceCatalog.length > 0 && (
                  <Select value={catalogSelection} onValueChange={addCatalogLine}>
                    <SelectTrigger className="md:flex-1">
                      <SelectValue placeholder="Ajouter depuis le catalogue..." />
                    </SelectTrigger>
                    <SelectContent>
                      {serviceCatalog.map((item) => (
                        <SelectItem key={item.id} value={item.id}>
                          {item.name} — {formatPrice(item.default_price || 0)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Button type="button" variant="outline" onClick={addEmptyLine}>
                  <Plus className="w-4 h-4 mr-2" />
                  Ligne libre
                </Button>
              </div>

              <div className="space-y-4">
                {lines.length === 0 && (
                  <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-slate-500">
                    Ajoute une ou plusieurs lignes pour commencer.
                  </div>
                )}
                {lines.map((line, index) => (
                  <div key={line.id} className="rounded-xl border border-slate-200 bg-white p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">Ligne {index + 1}</Badge>
                        <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">
                          {SERVICE_LINE_CATEGORIES.find((c) => c.value === line.category)?.label || line.category}
                        </Badge>
                      </div>
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeLine(line.id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2 md:col-span-2">
                        <Label>Désignation</Label>
                        <Input
                          value={line.designation}
                          onChange={(e) => updateLine(line.id, 'designation', e.target.value)}
                          placeholder="Ex: Machine, option, prestation..."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Catégorie</Label>
                        <Select value={line.category} onValueChange={(v) => updateLine(line.id, 'category', v)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {SERVICE_LINE_CATEGORIES.map((cat) => (
                              <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Unité</Label>
                        <Input
                          value={line.unit}
                          onChange={(e) => updateLine(line.id, 'unit', e.target.value)}
                          placeholder="u, h, forfait..."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Quantité</Label>
                        <Input
                          type="number" min="0" step="0.01"
                          value={line.quantity}
                          onChange={(e) => updateLine(line.id, 'quantity', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Prix unitaire HT</Label>
                        <Input
                          type="number" min="0" step="0.01"
                          value={line.unit_price}
                          onChange={(e) => updateLine(line.id, 'unit_price', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label>Description</Label>
                        <Textarea
                          value={line.description}
                          onChange={(e) => updateLine(line.id, 'description', e.target.value)}
                          placeholder="Détail complémentaire (optionnel)"
                          rows={2}
                        />
                      </div>
                    </div>

                    <div className="flex justify-end text-sm text-slate-600">
                      <span className="mr-2">Total ligne :</span>
                      <span className="font-mono font-semibold text-slate-900">{formatPrice(line.line_total)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Notes et conditions */}
          <Card className="border-slate-200">
            <CardHeader className="bg-slate-50/50 border-b border-slate-100">
              <CardTitle style={{ fontFamily: 'Barlow Condensed' }}>Notes et conditions</CardTitle>
            </CardHeader>
            <CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Validité (jours)</Label>
                <Input
                  type="number" min="1"
                  value={form.validity_days}
                  onChange={(e) => setForm((prev) => ({ ...prev, validity_days: Number(e.target.value || 30) }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Type de remise</Label>
                <Select value={form.discount_type} onValueChange={(v) => setForm((prev) => ({ ...prev, discount_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Pourcentage (%)</SelectItem>
                    <SelectItem value="fixed">Montant fixe (€)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Remise {form.discount_type === 'percent' ? '(%)' : '(€)'}</Label>
                <Input
                  type="number" min="0" step="0.01"
                  value={form.discount}
                  onChange={(e) => setForm((prev) => ({ ...prev, discount: Number(e.target.value || 0) }))}
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Notes internes</Label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                  placeholder="Conditions particulières, remarques..."
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Récapitulatif */}
        <div className="space-y-6">
          <Card className="border-slate-200 sticky top-6">
            <CardHeader className="bg-slate-50/50 border-b border-slate-100">
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                <FileText className="w-5 h-5 text-yellow-500" />
                Récapitulatif
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-3">
              {form.mini_pelle_name && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <p className="text-xs text-yellow-700 font-medium uppercase tracking-wide mb-1">Machine</p>
                  <p className="font-semibold text-slate-800">{form.mini_pelle_name}</p>
                </div>
              )}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-slate-600">
                  <span>Sous-total HT</span>
                  <span className="font-mono">{formatPrice(totals.subtotal)}</span>
                </div>
                {totals.discountAmount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Remise</span>
                    <span className="font-mono">- {formatPrice(totals.discountAmount)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg text-slate-900 border-t pt-2 mt-2">
                  <span>Total HT</span>
                  <span className="font-mono">{formatPrice(totals.total)}</span>
                </div>
              </div>

              <Button
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-slate-900 mt-2"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Créer le devis
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default DevisCreateSimplePage;
